import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Check, Flag, Info, Pencil, RotateCcw, X } from 'lucide-react';

import Markdown from 'components/Shared/Markdown';
import Price from 'components/Price/Price';
import { currencySymbol } from 'helpers/HelperFunctions';
import { timeUntil } from 'helpers/PitchLimitHelper';
import { setErrorMessage } from 'redux/modules/errorMessage';
import { setSuccessMessage } from 'redux/modules/successMessage';
import WebOnly from 'components/NativeApp/WebOnly';
import styles from './ApplicationStatus.module.scss';

const RESEND_COOLDOWN_SECONDS = 60;
const RESEND_STORAGE_KEY = 'contactDetailsLastResend';

const BLOCKED_REASONS = {
  wrong_location: 'Your location doesn’t match the location of the job.',
  spam: 'We decided your application was spam.',
  public_question:
    'It reads like a question. To find out more about a job, ask a public question.',
  no_relevant_work: 'Your portfolio needs work that’s relevant to this job.',
  not_enough_info: 'It needs to explain why you’re right for the job.',
  using_template: 'It needs to be written for this job, not from a template.',
  using_external_links: 'We don’t allow links to competitor platforms.',
  not_level_project:
    'Your experience level doesn’t match the level the job asks for.',
  brief_deleted: 'The client has taken this job down.',
};

const SENT_STAGES = {
  review: {
    label: 'Awaiting review',
    tone: 'neutral',
    tooltip: (noun) =>
      `Twine is checking your ${noun} before sending it to the client.`,
  },
  sent: {
    label: 'Sent to client',
    tone: 'blue',
    tooltip: (noun) =>
      `Twine has checked your ${noun} and sent it to the client.`,
  },
};

const HIRED_STAGE = {
  label: 'Hired',
  tone: 'green',
  tooltip: () => 'The client has hired you for this job.',
};

const ENDED_STAGE = {
  label: 'Hire ended',
  tone: 'neutral',
  tooltip: () => 'The client has ended this hire.',
};

const DECLINED_STAGE = {
  label: 'Declined',
  tone: 'neutral',
  tooltip: (noun) => `The client decided not to go ahead with your ${noun}.`,
};

const readStorage = (key) => {
  try {
    return localStorage.getItem(key);
  } catch (e) {
    return null;
  }
};

const writeStorage = (key, value) => {
  try {
    if (value === null) {
      localStorage.removeItem(key);
    } else {
      localStorage.setItem(key, value);
    }
  } catch (e) {
    // Storage can be blocked; the cooldown still runs for this page view.
  }
};

const onDateLabel = (prefix, timestamp) => {
  const date = moment(timestamp);
  if (!date.isValid() || date.isSame(moment(), 'day')) {
    return `${prefix} today`;
  }
  return `${prefix} on ${date.format('Do MMM YYYY')}`;
};

const sentOnLabel = (timestamp) => onDateLabel('Sent', timestamp);

export const getApplicationState = (pitch) => {
  if (pitch.completed_at) return 'ended';
  if (pitch.accepted_at) return 'hired';
  if (pitch.blocked_at !== '') return 'blocked';
  if (pitch.declined_at !== '') return 'declined';
  return 'sent';
};

const getSentStage = (pitch, user) => {
  if (
    pitch.approved_at !== '' ||
    pitch.receiver_viewed !== '' ||
    pitch.liked_at !== '' ||
    user.auto_approve_pitch_flag
  ) {
    return SENT_STAGES.sent;
  }
  return SENT_STAGES.review;
};

const useResendCooldown = () => {
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const timer = setTimeout(() => {
      if (cooldown === 1) writeStorage(RESEND_STORAGE_KEY, null);
      setCooldown(cooldown - 1);
    }, 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  useEffect(() => {
    const lastResend = parseInt(readStorage(RESEND_STORAGE_KEY), 10);
    if (!lastResend) return;
    const remaining =
      RESEND_COOLDOWN_SECONDS - Math.floor((Date.now() - lastResend) / 1000);
    if (remaining > 0) {
      setCooldown(remaining);
    } else {
      writeStorage(RESEND_STORAGE_KEY, null);
    }
  }, []);

  const start = () => {
    writeStorage(RESEND_STORAGE_KEY, Date.now().toString());
    setCooldown(RESEND_COOLDOWN_SECONDS);
  };

  return [cooldown, start];
};

const ContactList = ({ contactOptions }) => {
  const { email, phone, sms } = contactOptions || {};
  const ways = [
    email && 'Email',
    sms && 'WhatsApp or SMS',
    phone && 'Phone',
  ].filter(Boolean);

  if (ways.length === 0) {
    return 'The client wants to be contacted privately on Twine.';
  }
  return `The client wants to be contacted by ${ways.join(', ')}.`;
};

/**
 * The job page box once an application or interest is sent: sent (awaiting
 * review or sent to the client), being edited, blocked by Twine, declined,
 * hired or with the hire ended.
 */
const ApplicationStatus = ({
  clientName,
  contactOptions,
  currency = 'usd',
  editing,
  editingPitchAmount,
  editingPitchText,
  introsLeft,
  isInterest,
  isPaidBrief,
  onChangePitchAmount,
  onChangePitchText,
  onEdit,
  onEditCancel,
  onEditSave,
  onPitchAgain,
  onResendContactDetails,
  owner,
  pitch,
  pitchError,
  savingEdit,
  shouldOpenEditing,
  user,
}) => {
  const dispatch = useDispatch();
  const [resendCooldown, startResendCooldown] = useResendCooldown();
  const {
    resendingContactDetails,
    resendContactDetailsSuccess,
    resendContactDetailsError,
  } = useSelector((state) => state.briefDetails);

  useEffect(() => {
    if (shouldOpenEditing && onEdit && !isInterest) onEdit();
  }, []);

  useEffect(() => {
    if (resendingContactDetails) return;
    if (resendContactDetailsSuccess) {
      dispatch(
        setSuccessMessage(
          'Contact details will be available in your messages within a few minutes'
        )
      );
    }
    if (resendContactDetailsError) {
      dispatch(
        setErrorMessage(
          'There was an error re-requesting the client details. Please try again later.'
        )
      );
    }
  }, [
    resendingContactDetails,
    resendContactDetailsSuccess,
    resendContactDetailsError,
  ]);

  const handleResend = (event) => {
    event.preventDefault();
    if (resendCooldown > 0) return;
    onResendContactDetails();
    startResendCooldown();
  };

  const client = clientName || 'The client';
  const noun = isInterest ? 'interest' : 'application';
  const state = getApplicationState(pitch);
  const sharedResume = Boolean(pitch.share_resume && user.has_resume);
  const attachments = sharedResume
    ? 'your portfolio and CV/resume'
    : 'your portfolio';
  const messagesHref = owner?.username
    ? `/messages/${owner.username}`
    : '/messages';
  const isPro = Boolean(user.pro);
  const hasQuote = isPaidBrief && Number(pitch.budget) !== 0;

  const quota = !user.pro && !user.premium && (
    <div className={styles.quotaStrip}>
      <Info size={14} aria-hidden="true" className={styles.quotaIcon} />
      <span>
        <strong>
          {introsLeft} free application{introsLeft === 1 ? '' : 's'} left today.
        </strong>{' '}
        Resets in {timeUntil(new Date())}.
        <WebOnly>
          {' '}
          <Link to="/pricing/creative">Upgrade to Pro</Link> for 50 a month.
        </WebOnly>
      </span>
    </div>
  );

  const message = (
    <div className={styles.message}>
      <span className={styles.messageLabel}>Your message</span>
      <Markdown text={pitch.text} target="_blank" />
      {hasQuote && (
        <div className={styles.quote}>
          Your quote:{' '}
          <strong>
            <Price currency={currency} amount={pitch.budget} />
          </strong>
        </div>
      )}
    </div>
  );

  const header = (tone, Icon, title, subtitle, pill) => (
    <div className={styles.header}>
      <span
        className={`${styles.statusIcon} ${styles[`statusIcon_${tone}`]}`}
        aria-hidden="true"
      >
        <Icon size={24} strokeWidth={2.4} />
      </span>
      <div className={styles.headerText}>
        <h2 id="application-status-title" className={styles.title}>
          {title}
        </h2>
        <div className={styles.subtitle}>{subtitle}</div>
      </div>
      {pill && (
        <OverlayTrigger
          placement="bottom"
          overlay={
            <Tooltip id="application-status-tooltip">
              {pill.tooltip(noun)}
            </Tooltip>
          }
        >
          <span
            className={`${styles.pill} ${styles[`pill_${pill.tone}`]}`}
            tabIndex={0}
          >
            {pill.label}
          </span>
        </OverlayTrigger>
      )}
    </div>
  );

  const secondaryLinks = (showSimilarJobs = true) => (
    <>
      <a href="/job-tracker" className={styles.secondaryAction}>
        View my applications
      </a>
      {showSimilarJobs && (
        <a href="#similar-jobs" className={styles.linkAction}>
          See similar jobs
        </a>
      )}
    </>
  );

  if (editing) {
    return (
      <section
        className={`${styles.container} box-shadow`}
        aria-labelledby="application-status-title"
      >
        {header(
          'blue',
          Pencil,
          'Update your application',
          `${sentOnLabel(pitch.timestamp)}. The client sees your changes.`
        )}
        <div className={styles.field}>
          <label htmlFor="editPitchText" className={styles.fieldLabel}>
            Your application
          </label>
          <textarea
            id="editPitchText"
            className={`${styles.textarea} ${
              pitchError && pitchError.pitchText ? styles.fieldError : ''
            }`}
            disabled={savingEdit}
            onChange={(event) => onChangePitchText(event.target.value)}
            rows="6"
            value={editingPitchText}
          />
          {pitchError && pitchError.pitchText && (
            <div className={styles.errorMsg}>{pitchError.pitchText}</div>
          )}
        </div>
        {isPaidBrief && (
          <div className={styles.field}>
            <label htmlFor="editPitchAmount" className={styles.fieldLabel}>
              Your quote
            </label>
            <div className={styles.amountRow}>
              <span className={styles.currency}>
                {currencySymbol(currency)}
              </span>
              <input
                id="editPitchAmount"
                className={`${styles.amountInput} ${
                  pitchError && pitchError.pitchAmount ? styles.fieldError : ''
                }`}
                disabled={savingEdit}
                onChange={(event) => onChangePitchAmount(event.target.value)}
                placeholder="250"
                type="number"
                value={editingPitchAmount}
              />
            </div>
            <p className={styles.fieldHelper}>
              {`Set it to ${currencySymbol(
                currency
              )}0 if you’re still discussing it with the client.`}
            </p>
            {pitchError && pitchError.pitchAmount && (
              <div className={styles.errorMsg}>{pitchError.pitchAmount}</div>
            )}
          </div>
        )}
        <div className={styles.actions}>
          <button
            type="button"
            className={styles.primaryAction}
            disabled={savingEdit}
            onClick={onEditSave}
          >
            {savingEdit ? 'Saving...' : 'Save changes'}
          </button>
          <button
            type="button"
            className={styles.secondaryAction}
            disabled={savingEdit}
            onClick={onEditCancel}
          >
            Cancel
          </button>
        </div>
      </section>
    );
  }

  if (state === 'hired') {
    return (
      <section
        className={`${styles.container} box-shadow`}
        aria-labelledby="application-status-title"
      >
        {header(
          'green',
          Check,
          'You’ve been hired',
          onDateLabel('Hired', pitch.accepted_at),
          HIRED_STAGE
        )}
        <p className={styles.body}>{`${client} has hired you for this job.`}</p>
        <div className={styles.steps}>
          <span className={styles.stepsTitle}>What happens next</span>
          <ol className={styles.stepList}>
            <li className={styles.step}>
              <span className={styles.stepNumber} aria-hidden="true">
                1
              </span>
              <span>
                Discuss the job with the client in{' '}
                <a href={messagesHref}>Messages</a>.
              </span>
            </li>
            <li className={styles.step}>
              <span className={styles.stepNumber} aria-hidden="true">
                2
              </span>
              <span>
                Request a payment from the client with a{' '}
                <a href={`/manage/pitches/${pitch.id}/new-bill`}>new bill</a>.
              </span>
            </li>
          </ol>
        </div>
        <div className={styles.actions}>
          <a href={messagesHref} className={styles.primaryAction}>
            Go to Messages
          </a>
          <a
            href={`/manage/pitches/${pitch.id}/new-bill`}
            className={styles.secondaryAction}
          >
            New bill
          </a>
        </div>
      </section>
    );
  }

  if (state === 'ended') {
    return (
      <section
        className={`${styles.container} box-shadow`}
        aria-labelledby="application-status-title"
      >
        {header(
          'grey',
          Flag,
          'This hire has ended',
          onDateLabel('Ended', pitch.completed_at),
          ENDED_STAGE
        )}
        <p className={styles.body}>
          {`${client} has ended this hire. If you have more bills to send, ask them to hire you again.`}
        </p>
        <div className={styles.actions}>
          <a href={messagesHref} className={styles.primaryAction}>
            Go to Messages
          </a>
          {secondaryLinks()}
        </div>
      </section>
    );
  }

  if (state === 'blocked') {
    const reason = BLOCKED_REASONS[pitch.blocked_reason];
    const canReapply = pitch.blocked_reason !== 'spam' && onPitchAgain;

    return (
      <section
        className={`${styles.container} box-shadow`}
        aria-labelledby="application-status-title"
      >
        {header(
          'red',
          X,
          isInterest
            ? 'Your interest wasn’t sent'
            : 'Your application wasn’t sent',
          sentOnLabel(pitch.timestamp)
        )}
        <p className={styles.body}>
          Twine reviewed your {noun} and didn’t send it to {client}.
        </p>
        {reason && (
          <div className={styles.reason} role="note">
            <span className={styles.messageLabel}>Why</span>
            {reason}
          </div>
        )}
        {message}
        {canReapply && (
          <p className={styles.body}>
            You can fix this and apply again. A new application doesn’t use up
            another of your applications.
          </p>
        )}
        <div className={styles.actions}>
          {canReapply && (
            <button
              type="button"
              className={styles.primaryAction}
              onClick={onPitchAgain}
            >
              <RotateCcw size={16} aria-hidden="true" />
              Apply again
            </button>
          )}
          {secondaryLinks()}
        </div>
      </section>
    );
  }

  if (state === 'declined') {
    return (
      <section
        className={`${styles.container} box-shadow`}
        aria-labelledby="application-status-title"
      >
        {header(
          'grey',
          X,
          'Not this time',
          sentOnLabel(pitch.timestamp),
          DECLINED_STAGE
        )}
        <p className={styles.body}>
          {`${client} decided not to go ahead with your ${noun}. They may be working with another freelancer now.`}
        </p>
        {message}
        <div className={styles.steps}>
          <span className={styles.stepsTitle}>What you can do</span>
          <ul className={styles.tipList}>
            <li>Look at similar jobs below. New ones go up every day.</li>
            <li>
              Add your best, most relevant work to your portfolio so it’s the
              first thing clients see.
            </li>
          </ul>
        </div>
        {quota}
        <div className={styles.actions}>{secondaryLinks()}</div>
      </section>
    );
  }

  const stage = getSentStage(pitch, user);
  const awaitingReview = stage === SENT_STAGES.review;
  const hasContactOptions = Object.values(contactOptions || {}).some(Boolean);

  const steps = [
    awaitingReview && `Twine checks your ${noun}, then sends it to ${client}.`,
    isInterest
      ? 'The client looks through everyone who’s shown interest.'
      : 'The client reviews the applications they get.',
    <>
      If they want to talk, they message you and we email you. You’ll find it in{' '}
      <a href="/messages">Messages</a>.
    </>,
    <>
      Track it in <a href="/job-tracker">Job Tracker</a> under Applied.
    </>,
  ].filter(Boolean);

  const canEdit =
    !isInterest && onEdit && moment().diff(pitch.timestamp, 'minutes') > 5;
  const showGoToMessages = isPro && !awaitingReview;

  return (
    <section
      className={`${styles.container} box-shadow`}
      aria-labelledby="application-status-title"
    >
      {header(
        'green',
        Check,
        isInterest ? 'You’ve shown interest' : 'Your application has been sent',
        `${sentOnLabel(pitch.timestamp)} with ${attachments}`,
        stage
      )}

      {message}

      <div className={styles.steps}>
        <span className={styles.stepsTitle}>What happens next</span>
        <ol className={styles.stepList}>
          {steps.map((step, index) => (
            <li key={index} className={styles.step}>
              <span className={styles.stepNumber} aria-hidden="true">
                {index + 1}
              </span>
              <span>{step}</span>
            </li>
          ))}
        </ol>
      </div>

      <div className={styles.contact}>
        <span className={styles.stepsTitle}>Contacting the client</span>
        <ul className={styles.tipList}>
          <li>
            <ContactList contactOptions={contactOptions} />
          </li>
          {isPro && hasContactOptions && (
            <li>
              {awaitingReview ? (
                <>
                  Their contact details will be in{' '}
                  <a href="/messages">your messages</a> once Twine approves your{' '}
                  {noun}.
                </>
              ) : (
                <>
                  Their contact details are in{' '}
                  <a href={messagesHref}>your messages</a>.
                </>
              )}
            </li>
          )}
          {isPro ? (
            <li>
              Not in your messages?{' '}
              <a
                href="#"
                onClick={handleResend}
                aria-disabled={resendCooldown > 0}
              >
                {resendCooldown > 0
                  ? `Re-request the client details (${resendCooldown}s)`
                  : 'Re-request the client details'}
              </a>
            </li>
          ) : (
            <>
              <li>
                Only{' '}
                <WebOnly fallback="Twine Pro">
                  <a href="/pricing/creative">Twine Pro</a>
                </WebOnly>{' '}
                members get the client’s contact details.
              </li>
              <li>
                Not heard back in 3 days?{' '}
                <a href={messagesHref}>Send the client another message</a>.
              </li>
            </>
          )}
        </ul>
      </div>

      {quota}

      <div className={styles.actions}>
        {showGoToMessages && (
          <a href={messagesHref} className={styles.primaryAction}>
            Go to Messages
          </a>
        )}
        {secondaryLinks(!canEdit)}
        {canEdit && (
          <button type="button" className={styles.linkAction} onClick={onEdit}>
            Update application
          </button>
        )}
      </div>
    </section>
  );
};

ContactList.propTypes = {
  contactOptions: PropTypes.object,
};

ApplicationStatus.propTypes = {
  clientName: PropTypes.string,
  contactOptions: PropTypes.object,
  currency: PropTypes.string,
  editing: PropTypes.bool,
  editingPitchAmount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  editingPitchText: PropTypes.string,
  introsLeft: PropTypes.number,
  isInterest: PropTypes.bool,
  isPaidBrief: PropTypes.bool,
  onChangePitchAmount: PropTypes.func,
  onChangePitchText: PropTypes.func,
  onEdit: PropTypes.func,
  onEditCancel: PropTypes.func,
  onEditSave: PropTypes.func,
  onPitchAgain: PropTypes.func,
  onResendContactDetails: PropTypes.func,
  owner: PropTypes.object,
  pitch: PropTypes.object.isRequired,
  pitchError: PropTypes.object,
  savingEdit: PropTypes.bool,
  shouldOpenEditing: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  user: PropTypes.object.isRequired,
};

export default ApplicationStatus;
